import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type TouchEvent as ReactTouchEvent } from "react";
import { createPortal } from "react-dom";
import { useNarrowScreen } from "../hooks/useNarrowScreen";

type LightboxDragAxis = "undecided" | "vertical" | "horizontal";

export type ChatImageLightboxProps = {
  open: boolean;
  onClose: () => void;
  url: string;
};

export function ChatImageLightbox({ open, onClose, url }: ChatImageLightboxProps) {
  const [lightboxDragY, setLightboxDragY] = useState(0);
  const [lightboxSnapBack, setLightboxSnapBack] = useState(false);
  const narrow = useNarrowScreen();
  const lightboxDragRef = useRef<{
    pointerId: number | null;
    startX: number;
    startY: number;
    axis: LightboxDragAxis;
    maxAbsDy: number;
  }>({ pointerId: null, startX: 0, startY: 0, axis: "undecided", maxAbsDy: 0 });
  const blockLightboxBackdropClickRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setLightboxDragY(0);
    setLightboxSnapBack(false);
    blockLightboxBackdropClickRef.current = false;
    lightboxDragRef.current = {
      pointerId: null,
      startX: 0,
      startY: 0,
      axis: "undecided",
      maxAbsDy: 0,
    };
  }, [open]);

  const lightboxSwipeDismissThreshold = () =>
    typeof window !== "undefined" ? Math.min(100, window.innerHeight * 0.15) : 100;

  const onLightboxPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!narrow) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    lightboxDragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      axis: "undecided",
      maxAbsDy: 0,
    };
    setLightboxSnapBack(false);
  };

  const onLightboxPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!narrow) return;
    const st = lightboxDragRef.current;
    if (st.pointerId !== e.pointerId) return;
    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;
    if (st.axis === "undecided") {
      if (dx * dx + dy * dy < 64) return;
      st.axis = Math.abs(dy) >= Math.abs(dx) ? "vertical" : "horizontal";
    }
    if (st.axis !== "vertical") return;
    st.maxAbsDy = Math.max(st.maxAbsDy, Math.abs(dy));
    setLightboxDragY(dy);
  };

  const endLightboxPointer = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!narrow) return;
    const st = lightboxDragRef.current;
    if (st.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    st.pointerId = null;
    const dy = e.clientY - st.startY;
    const axis = st.axis;
    st.axis = "undecided";

    if (axis === "vertical") {
      const th = lightboxSwipeDismissThreshold();
      if (Math.abs(dy) >= th) {
        onClose();
        setLightboxDragY(0);
      } else {
        if (st.maxAbsDy > 8) blockLightboxBackdropClickRef.current = true;
        if (Math.abs(dy) > 0 || st.maxAbsDy > 0) {
          setLightboxSnapBack(true);
          requestAnimationFrame(() => setLightboxDragY(0));
          window.setTimeout(() => setLightboxSnapBack(false), 220);
        } else {
          setLightboxDragY(0);
        }
      }
    } else {
      setLightboxDragY(0);
    }
  };

  const onLightboxTouchBubbleStop = (e: ReactTouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const lightboxBackdropClick = () => {
    if (blockLightboxBackdropClickRef.current) {
      blockLightboxBackdropClickRef.current = false;
      return;
    }
    onClose();
  };

  const vh = typeof window !== "undefined" ? window.innerHeight : 600;
  const lightboxOverlayStyle = narrow
    ? {
        transform: `translateY(${lightboxDragY}px)`,
        opacity:
          lightboxDragY === 0
            ? 1
            : 1 - Math.min(0.45, Math.abs(lightboxDragY) / Math.max(1, vh * 0.35)),
      }
    : undefined;

  if (!open) return null;

  return createPortal(
    <div
      className={["img-lightbox", narrow && lightboxSnapBack ? "img-lightbox--snap-back" : ""]
        .filter(Boolean)
        .join(" ")}
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр изображения"
      style={lightboxOverlayStyle}
      onClick={lightboxBackdropClick}
      onPointerDown={onLightboxPointerDown}
      onPointerMove={onLightboxPointerMove}
      onPointerUp={endLightboxPointer}
      onPointerCancel={endLightboxPointer}
      onTouchStart={onLightboxTouchBubbleStop}
      onTouchMove={onLightboxTouchBubbleStop}
      onTouchEnd={onLightboxTouchBubbleStop}
      onTouchCancel={onLightboxTouchBubbleStop}
    >
      <button
        type="button"
        className="img-lightbox__close"
        aria-label="Закрыть"
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        ×
      </button>
      <img
        src={url}
        alt=""
        className="img-lightbox__full"
        draggable={false}
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body,
  );
}
